import { AlertTriangle, CheckCircle2, ExternalLink, Lock, Network, ShieldQuestion, XCircle } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { EmptyState } from '../ui/EmptyState';
import { cn } from '../../utils/cn';
import { formatRelativeTime } from '../../utils/format';
import type {
  KnownVulnerability,
  SecurityHeaderCheck,
  ServerExposureReport,
  SiteVulnerabilityReport,
  VulnSeverity,
} from '../../types';

const severityTone: Record<VulnSeverity, 'danger' | 'warning' | 'neutral'> = {
  critical: 'danger',
  high: 'danger',
  medium: 'warning',
  low: 'neutral',
  info: 'neutral',
};

const sslGradeTone = (grade: string | null): 'success' | 'warning' | 'danger' | 'neutral' => {
  if (!grade) return 'neutral';
  if (['A+', 'A', 'A-'].includes(grade)) return 'success';
  if (['B'].includes(grade)) return 'warning';
  return 'danger';
};

function SslBadge({ ssl }: { ssl: SiteVulnerabilityReport['ssl'] }) {
  if (!ssl) return <Badge tone="neutral">Not scanned</Badge>;
  if (ssl.status === 'in_progress') return <Badge tone="neutral">Scanning...</Badge>;
  if (ssl.status === 'error' || ssl.status === 'unavailable') return <Badge tone="neutral">Unavailable</Badge>;
  return <Badge tone={sslGradeTone(ssl.grade)}>SSL Labs: {ssl.grade ?? '?'}</Badge>;
}

function HeaderChecklist({ headers }: { headers: SecurityHeaderCheck[] }) {
  return (
    <ul className="space-y-1.5">
      {headers.map((h) => (
        <li key={h.header} className="flex items-start gap-2 text-sm">
          {h.present ? (
            <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-pulse-500" />
          ) : (
            <XCircle size={14} className="mt-0.5 shrink-0 text-amber-500" />
          )}
          <div>
            <span className="font-mono text-xs text-slate-600 dark:text-slate-300">{h.header}</span>
            {!h.present && <p className="text-xs text-slate-400">{h.recommendation}</p>}
          </div>
        </li>
      ))}
    </ul>
  );
}

function VulnerabilityRow({ v }: { v: KnownVulnerability }) {
  return (
    <div className="flex items-start gap-2.5 rounded-lg border border-slate-100 p-2.5 dark:border-slate-800">
      <AlertTriangle size={14} className="mt-0.5 shrink-0 text-rose-500" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-sm font-medium text-slate-800 dark:text-slate-100">{v.component}</span>
          <Badge tone={severityTone[v.severity]}>{v.severity}</Badge>
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400">{v.title}</p>
        <p className="mt-1 text-xs text-slate-400">
          Installed: {v.version} {v.fixedIn && <>&middot; Fixed in {v.fixedIn}</>}
          {v.referenceUrl && (
            <a href={v.referenceUrl} target="_blank" rel="noreferrer" className="ml-1.5 inline-flex items-center gap-0.5 text-pulse-600 hover:underline dark:text-pulse-400">
              Reference <ExternalLink size={10} />
            </a>
          )}
        </p>
      </div>
    </div>
  );
}

function SiteReportCard({ report }: { report: SiteVulnerabilityReport }) {
  const missingHeaders = report.headers.filter((h) => !h.present).length;

  return (
    <div className="rounded-xl border border-slate-100 p-4 dark:border-slate-800">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-semibold text-slate-900 dark:text-white">{report.site}</p>
        <div className="flex items-center gap-1.5">
          <SslBadge ssl={report.ssl} />
          {missingHeaders > 0 && <Badge tone="warning">{missingHeaders} header(s) missing</Badge>}
          {report.knownVulnerabilities.length > 0 && <Badge tone="danger">{report.knownVulnerabilities.length} known CVE(s)</Badge>}
        </div>
      </div>

      {report.ssl && report.ssl.issues.length > 0 && (
        <ul className="mt-2 space-y-1">
          {report.ssl.issues.map((issue, i) => (
            <li key={i} className="flex items-start gap-1.5 text-xs text-rose-600 dark:text-rose-400">
              <Lock size={11} className="mt-0.5 shrink-0" /> {issue}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div>
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">Security headers</p>
          <HeaderChecklist headers={report.headers} />
        </div>
        <div>
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">Known vulnerabilities</p>
          {report.knownVulnerabilities.length === 0 ? (
            <p className="text-xs text-slate-400">No known CVEs found in installed plugins.</p>
          ) : (
            <div className="space-y-2">
              {report.knownVulnerabilities.map((v, i) => (
                <VulnerabilityRow key={i} v={v} />
              ))}
            </div>
          )}
        </div>
      </div>

      <p className="mt-3 text-[11px] text-slate-400">Scanned {formatRelativeTime(report.scannedAt)}</p>
    </div>
  );
}

function ServerExposureCard({ server }: { server: ServerExposureReport }) {
  const unexpected = server.ports.filter((p) => p.open && !p.expected);
  return (
    <div className="rounded-xl border border-slate-100 p-4 dark:border-slate-800">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-1.5 font-semibold text-slate-900 dark:text-white">
          <Network size={15} /> {server.host}
        </p>
        {unexpected.length > 0 ? (
          <Badge tone="warning">{unexpected.length} unexpected open</Badge>
        ) : (
          <Badge tone="success">As expected</Badge>
        )}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
        {server.ports.map((p) => (
          <div
            key={p.port}
            className={cn(
              'rounded-lg border px-2.5 py-2 text-xs',
              p.open
                ? p.expected
                  ? 'border-pulse-200 bg-pulse-50 dark:border-pulse-900/50 dark:bg-pulse-900/20'
                  : 'border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-900/20'
                : 'border-slate-100 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/40',
            )}
          >
            <p className="font-medium text-slate-700 dark:text-slate-200">{p.label}</p>
            <p className="text-slate-400">
              :{p.port} &middot; {p.open ? 'Open' : 'Closed'}
            </p>
          </div>
        ))}
      </div>
      <p className="mt-3 text-[11px] text-slate-400">Scanned {formatRelativeTime(server.scannedAt)}</p>
    </div>
  );
}

export function VulnScanReport({
  sites,
  server,
  wpVulnDbConfigured,
}: {
  sites: SiteVulnerabilityReport[];
  server: ServerExposureReport | null;
  wpVulnDbConfigured: boolean;
}) {
  if (sites.length === 0 && !server) {
    return (
      <EmptyState
        icon={ShieldQuestion}
        title="Vulnerability scanning isn't configured yet"
        description="SSL and header checks activate automatically once WordPress sites are configured. Set WPVULNDB_API_TOKEN for known-CVE lookups and VPS_HOST for port exposure - see README."
      />
    );
  }

  return (
    <div className="space-y-4">
      {!wpVulnDbConfigured && sites.length > 0 && (
        <div className="flex items-start gap-2 rounded-lg bg-slate-50 p-3 text-xs text-slate-500 dark:bg-slate-800/60 dark:text-slate-400">
          <ShieldQuestion size={14} className="mt-0.5 shrink-0" />
          Known-CVE checking is off - set <code className="rounded bg-slate-100 px-1 dark:bg-slate-700">WPVULNDB_API_TOKEN</code> in
          backend/.env (free at wpscan.com/api) to check installed plugin versions against known vulnerabilities.
        </div>
      )}
      {sites.map((site) => (
        <SiteReportCard key={site.site} report={site} />
      ))}
      {server && <ServerExposureCard server={server} />}
    </div>
  );
}
